import { Modal, Select, Input, message, Divider } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import axios from "axios"; // ✅ Use axios for API calls
import record_selected from "../img/record_selected_64.png";
import authoring_selected from "../img/authoring_selected_64.png";
import read_aloud_selected from "../img/read_aloud_selected_64.png";
import word_clarification_selected from "../img/word_clarification_selected_64.png";
import word_wall_selected from "../img/word_wall_selected_64.png";


const ALL_CHARACTERS_TASK_TYPES = new Set(["hexiLexi", "vorlesen", "analyze"]);


const AddTask = ({ visible, onClose, id, StoryOptionLabel }) => {
  const [stories, setStories] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [members, setMembers] = useState([]);

  const [existingTasks, setExistingTasks] = useState([]);

  const [selectedStory, setSelectedStory] = useState(null);
  const [taskDescription, setTaskDescription] = useState("");
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [taskType, setTaskType] = useState(null);
  const [assignedMember, setAssignedMember] = useState(null);

  const isAllCharactersTask = ALL_CHARACTERS_TASK_TYPES.has(taskType);

  // Fetch all stories when the modal opens
  useEffect(() => {
    setSelectedStory(null);
    if (visible) {
      axios
        .get("/api/stories")
        .then((res) => setStories(res.data))
        .catch((error) => {
          message.error("Fehler beim Laden der Geschichten.");
          console.error("Error fetching stories:", error);
        });
    }
  }, [visible]);

  useEffect(() => {
    if (!taskType) return;

    if (ALL_CHARACTERS_TASK_TYPES.has(taskType)) {
      setSelectedCharacters(["alles"]);
    } else {
      setSelectedCharacters([]);
    }
  }, [taskType]);

  // When a story is selected, fetch its characters
  const handleStoryChange = async (storyId) => {
    setSelectedStory(storyId);
    setCharacters([]); // Reset characters when a new story is selected
    setMembers([]); // Reset members when a new story is selected
    setExistingTasks([]); // Reset existing tasks
    setSelectedCharacters(
      ALL_CHARACTERS_TASK_TYPES.has(taskType) ? ["alles"] : []
    ); // Reset selected characters when a new story is selected

    try {
      const charResponse = await axios.get(
        `/api/characters/?storycharid=${storyId}`
      );
      console.log("charResponse:", charResponse);
      setCharacters(charResponse.data);

      const membersResponse = await axios.get(`/api/groups/${id}/members`);
      console.log("membersResponse:", membersResponse);
      setMembers(membersResponse.data);

      // Fetch existing tasks for this story within the current group
      const tasksResponse = await axios.get(`/api/tasks/${id}/${storyId}`);
      console.log("tasksResponse:", tasksResponse);
      setExistingTasks(tasksResponse.data); // Store tasks so we can filter characters
    } catch (error) {
      message.error("Fehler beim Laden der Daten.");
      console.error("Error fetching characters or members:", error);
    }
  };

  const handleAssignTask = (characterId, memberId, taskType, description) => {
    const isAllCharactersCategory = ALL_CHARACTERS_TASK_TYPES.has(taskType);

    if (!memberId || !taskType || !description) {
      message.error("Bitte füllen Sie alle Felder aus.");
      return;
    }

    if (!isAllCharactersCategory && !characterId) {
      message.error("Bitte füllen Sie alle Felder aus.");
      return;
    }

    console.log("group id:", id);

    const endpoint =
      isAllCharactersCategory || !characterId || characterId === "alles"
        ? `/api/tasks/${selectedStory}/characters/assign`
        : `/api/tasks/${selectedStory}/characters/${characterId}/assign`;

    axios
      .post(endpoint, {
        memberId,
        id,
        category: taskType,
        description,
      })
      .then((response) => {
        message.success("Aufgabe erfolgreich zugewiesen!");
        handleClose();
      })
      .catch((error) => {
        console.error("Error assigning task:", error);
        message.error("Fehler beim Zuweisen der Aufgabe.");
      });
  };

  // ✅ Submit task
  const handleSubmit = () => {
    if (!selectedStory || !taskDescription || !taskType || !assignedMember) {
      message.error("Bitte fülle alle Felder aus.");
      return;
    }

    if (!isAllCharactersTask && !selectedCharacters.length) {
      message.error("Bitte fülle alle Felder aus.");
      return;
    }
    
    if (isAllCharactersTask) {
      // No character restriction for these task types
      handleAssignTask("alles", assignedMember, taskType, taskDescription);
      return;
    }

    // Call handleAssignTask for each selected character
    selectedCharacters.forEach((characterId) => {
      handleAssignTask(characterId, assignedMember, taskType, taskDescription);
    });
  };

  const handleClose = () => {
    // Reset the state variables
    //setSelectedStory(null);
    setTaskDescription("");
    setSelectedCharacters([]);
    setTaskType(null);
    setAssignedMember(null);

    onClose(); // Call the parent onClose function
  };

  // Filter characters to exclude those that already have a task of the selected type
  const getAvailableCharacters = () => {
    if (isAllCharactersTask) return [];
    if (!taskType) return characters;

    // Convert character_ids from CSV string to an array
    const charactersWithTask = new Set(
      existingTasks
        .filter((task) => task.category === taskType)
        .flatMap((task) =>
          task.character_ids ? task.character_ids.split(",").map(Number) : []
        )
    );

    console.log(
      "Filtered Available Characters:",
      characters.filter((char) => !charactersWithTask.has(char.id))
    );

    return characters.filter(
      (char) => !charactersWithTask.has(Number(char.id))
    );
  };

  const taskTypeIconStyle = {
    width: 20,
    height: 20,
    objectFit: "contain",
  };

  const storyIconStyle = {
    width: 24,
    height: 24,
    objectFit: "cover",
    borderRadius: 4,
  };

  const storyIconStyleSelected = {
    width: 18,
    height: 18,
    objectFit: "cover",
    borderRadius: 4,
  };

  return (
    <Modal
      title="Neue Aufgabe hinzufügen"
      open={visible}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={
        <span>
          <PlusOutlined /> Aufgaben zuweisen
        </span>
      }
      cancelText="Abbrechen"
    >



      {/* Story Selection */}
      <Select
        placeholder="Wähle eine Geschichte"
        style={{ width: "100%", marginBottom: "10px" }}
        value={selectedStory}
        onChange={handleStoryChange}
        optionLabelProp="label"
        loading={!stories.length} // Show loading state while fetching
      >
        {stories.map((story, index) => (
          <Select.Option
            key={story.id}
            value={story.id}
            label={
              <StoryOptionLabel
                text={story.title}
                imgSrc={story.img ? `/api/stories/imagefile/${story.img}` : null}
                iconStyle={storyIconStyleSelected}
              />
            }
          >
            <div>
              <StoryOptionLabel
                text={story.title}
                imgSrc={story.img ? `/api/stories/imagefile/${story.img}` : null}
                iconStyle={storyIconStyle}
              />
              {index < stories.length - 1 ? (
                <Divider style={{ margin: "8px 0 0" }} />
              ) : null}
            </div>
          </Select.Option>
        ))}
      </Select>

      {/* Task Description */}
      <Input
        placeholder="Aufgabenbeschreibung"
        value={taskDescription}
        onChange={(e) => setTaskDescription(e.target.value)}
        style={{ marginBottom: "10px" }}
      />

      {/* Task Type */}
      <Select
        placeholder="Aufgabentyp wählen"
        style={{ width: "100%", marginBottom: "10px" }}
        value={taskType}
        onChange={(value) => setTaskType(value)}
      >
        <Select.Option value="record">
          <StoryOptionLabel
            text="Aufnehmen"
            imgSrc={record_selected}
            iconStyle={taskTypeIconStyle}
          />
        </Select.Option>
       
        <Select.Option value="feedback">
          <StoryOptionLabel
            text="Feedback geben"
            iconStyle={taskTypeIconStyle}
            fallbackIcon={<span style={{ fontSize: 18, lineHeight: 1 }}>✏</span>}
          />
        </Select.Option>
        <Select.Option value="analyze">
          <StoryOptionLabel
            text="Wortwolke"
            imgSrc={word_wall_selected}
            iconStyle={taskTypeIconStyle}
          />
        </Select.Option>
        <Select.Option value="enrichments">
          <StoryOptionLabel
            text="Gestalten"
            imgSrc={authoring_selected}
            iconStyle={taskTypeIconStyle}
          />
        </Select.Option>
        <Select.Option value="hexiLexi">
          <StoryOptionLabel
            text="HexiLexi"
            imgSrc={word_clarification_selected}
            iconStyle={taskTypeIconStyle}
          />
        </Select.Option>
        <Select.Option value="vorlesen">
          <StoryOptionLabel
            text="Vorlesen"
            imgSrc={read_aloud_selected}
            iconStyle={taskTypeIconStyle}
          />
        </Select.Option>
      </Select>

      {/* Character Selection (Loaded based on selected story) */}
      <Select
        disabled={!taskType || isAllCharactersTask} // Disable until task type is selected
        mode="multiple"
        placeholder="Charaktere auswählen"
        style={{ width: "100%", marginBottom: "10px" }}
        value={selectedCharacters} // Bind to selectedCharacters state
        onChange={(value) => setSelectedCharacters(value)} // Update state on change
      >
        {isAllCharactersTask ? (
          <Select.Option key="alles" value="alles">
            Alle
          </Select.Option>
        ) : (
          getAvailableCharacters().map((char) => (
            <Select.Option key={char.id} value={char.id}>
              {char.name}
            </Select.Option>
          ))
        )}
      </Select>

      {/* Member Selection (Loaded based on selected story) */}
      <Select
        placeholder="Mitglied auswählen"
        style={{ width: "100%", marginBottom: "10px" }}
        value={assignedMember}
        onChange={(value) => setAssignedMember(value)}
        loading={!members.length && selectedStory} // Show loading state
      >
        {members.map((member) => (
          <Select.Option key={member.id} value={member.id}>
            {member.username}
          </Select.Option>
        ))}
      </Select>

      
    </Modal>
  );
};

export default AddTask;
